import {
  GET_REGISTER_ASSIGNMENTS_BY_PERMIT_REGISTER_ID,
  useGetRegisterAssignmentsByPermitRegisterId,
} from "../operations/queries/getRegisterAssignmentsByPermitRegisterId";
import { useLazyGetOrgUsersByOrganizationId } from "../operations/queries/getOrgUsersByOrganizationId";
import { GET_PROJECTS_BY_ORGANIZATION_ID } from "../operations/queries/getProjectsByOrganizationId";
import { GET_PROJECT, useLazyGetProject } from "../operations/queries/getProject";
import { useAddRegisterAssignments } from "../operations/mutations/addRegisterAssignments";
import { useDeleteRegisterAssignment } from "../operations/mutations/deleteRegisterAssignment";
import { useAddPermitTypeToRegister } from "../operations/mutations/addPermitTypeToRegister";
import ProjectView from "../components/ProjectView";
import { Project } from "../models/Project";
import { PermitType } from "../models/PermitType";
import { useEffect } from "react";
import { useAppContext } from "../hooks/useAppContext";
import { PermitRegister } from "../models/PermitRegister";

interface Props {
  showProjectBoundariesOnMap?: boolean;
  isPopup?: boolean;
  project?: Project;
  permitRegister?: PermitRegister;
  permitTypes?: PermitType[];
  onClose: () => void;
}

const ProjectViewContainer = ({ showProjectBoundariesOnMap, isPopup, project, permitRegister, permitTypes, onClose }: Props) => {
  const { currentOrganization } = useAppContext();

  const { getProject, data: projectData } = useLazyGetProject();
  const { getRegisterAssignmentsByPermitRegisterId: getRegisterAssignments, data: registerAssignmentsData } =
    useGetRegisterAssignmentsByPermitRegisterId();
  const { getOrgUsersByOrganizationId: getOrgUsers, data: orgUsers } = useLazyGetOrgUsersByOrganizationId();
  const { mutate: addRegisterAssignmentsMutate, status: addRegisterAssignmentsStatus } = useAddRegisterAssignments();
  const { mutate: deleteRegisterAssignmentMutate, status: deleteRegisterAssignmentStatus } =
    useDeleteRegisterAssignment();
  const { mutate: addPermitTypeToRegisterMutate, status: addPermitTypesToRegisterStatus } =
    useAddPermitTypeToRegister();

  useEffect(() => {
    if (project?.id) {
      getProject({ variables: { id: project.id } });
    }
  }, [project?.id, getProject]);

  useEffect(() => {
    if (permitRegister?.id) {
      getRegisterAssignments({ variables: { permitRegisterId: permitRegister.id } });
    }
  }, [permitRegister?.id, getRegisterAssignments]);

  useEffect(() => {
    if (currentOrganization) {
      getOrgUsers({ variables: { organizationId: currentOrganization.id } });
    }
  }, [currentOrganization]);

  const handleAddRegisterAssignments = (orgUserIds: string[], permitTypeOrgRoleId: string) => {
    if (!permitRegister) return;
    addRegisterAssignmentsMutate({
      refetchQueries: [
        {
          query: GET_REGISTER_ASSIGNMENTS_BY_PERMIT_REGISTER_ID,
          variables: {
            permitRegisterId: permitRegister.id,
          },
        },
        {
          query: GET_PROJECT,
          variables: { id: project?.id },
        },
      ],
      variables: {
        orgUserIds,
        permitRegisterId: permitRegister.id,
        permitTypeOrgRoleId,
      },
    });
  };

  const handleDeleteRegisterAssignment = (registerAssignmentId: string) => {
    if (!permitRegister) return;
    deleteRegisterAssignmentMutate({
      refetchQueries: [
        {
          query: GET_REGISTER_ASSIGNMENTS_BY_PERMIT_REGISTER_ID,
          variables: {
            permitRegisterId: permitRegister.id,
          },
        },
        {
          query: GET_PROJECT,
          variables: { id: project?.id },
        },
      ],
      variables: {
        registerAssignmentId,
        permitRegisterId: permitRegister.id,
      },
    });
  };

  const handleAddPermitTypesToRegister = (permitTypeIds: string[]) => {
    if (!permitRegister) return;
    addPermitTypeToRegisterMutate({
      refetchQueries: [
        {
          query: GET_PROJECTS_BY_ORGANIZATION_ID,
          variables: { organizationId: currentOrganization?.id },
        },
      ],
      variables: {
        permitRegisterId: permitRegister.id,
        permitTypeIds,
      },
    });
  };

  const availablePermitTypes = permitTypes?.filter(
    (t) => !permitRegister?.permitTypes?.some((rt) => rt.id === t.id),
  );

  return (
    <ProjectView
      showProjectBoundariesOnMap={showProjectBoundariesOnMap}
      isPopup={isPopup}
      project={projectData?.project ?? project}
      permitRegister={permitRegister}
      registerAssignments={registerAssignmentsData?.registerAssignmentsByPermitRegisterId}
      orgUsers={orgUsers?.orgUsersByOrganizationId}
      onClose={onClose}
      onAddRegisterAssignments={handleAddRegisterAssignments}
      addRegisterAssignmentsStatus={addRegisterAssignmentsStatus}
      onDeleteRegisterAssignment={handleDeleteRegisterAssignment}
      deleteRegisterAssignmentStatus={deleteRegisterAssignmentStatus}
      availablePermitTypes={availablePermitTypes}
      onAddPermitTypesToRegister={handleAddPermitTypesToRegister}
      addPermitTypesToRegisterStatus={addPermitTypesToRegisterStatus}
    />
  );
};

export default ProjectViewContainer;
